import { Schema } from "effect";
import { MongoCallRecordId, MongoUserId } from "../../../../ids/mongo-ids";
import { EmailAddressEntry } from "./email";
import {
  MessageFacebookCommentAttribute,
  MessageFacebookCommentState,
} from "./facebook-comment";
import {
  MessageInstagramCommentAttribute,
  MessageInstagramCommentState,
} from "./instagram-comment";
import {
  MessageMetaCommentAttribute,
  MessageMetaCommentState,
  MessageMetaCommentType,
} from "./meta-comment";
import { MessageRedoChatConfirmationState } from "./redo-chat";
import { MessageSmsService } from "./sms";

// Gmail channel data
export const GmailChannelData = Schema.Struct({
  _tag: Schema.Literal("gmail"),
  gmailMessageId: Schema.String,
  gmailHistoryId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * URL to the HTML body content of the email message.
   * If there's content in the body, this should always exist.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Parsed, sanitized HTML of the email message, stripping
   * out the reply quotations.
   */
  parsedHtmlBody: Schema.NullOr(
    Schema.String.pipe(Schema.maxLength(16 * 1024)),
  ),
  /**
   * Email addresses associated with this message (from, to, cc, bcc).
   */
  addresses: Schema.Array(EmailAddressEntry),
});
export type GmailChannelData = typeof GmailChannelData.Type;

// Outlook channel data
export const OutlookChannelData = Schema.Struct({
  _tag: Schema.Literal("outlook"),
  outlookMessageId: Schema.String,
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * URL to the HTML body content of the email message.
   * If there's content in the body, this should always exist.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Parsed, sanitized HTML of the email message, stripping
   * out the reply quotations.
   */
  parsedHtmlBody: Schema.NullOr(
    Schema.String.pipe(Schema.maxLength(16 * 1024)),
  ),
  /**
   * Email addresses associated with this message (from, to, cc, bcc).
   */
  addresses: Schema.Array(EmailAddressEntry),
});
export type OutlookChannelData = typeof OutlookChannelData.Type;

// Email channel data - union of Gmail and Outlook
export const EmailChannelData = Schema.Union(
  GmailChannelData,
  OutlookChannelData,
);
export type EmailChannelData = typeof EmailChannelData.Type;

// SMS channel data
export const SmsChannelData = Schema.Struct({
  _tag: Schema.Literal("sms"),
  messageService: MessageSmsService,
});
export type SmsChannelData = typeof SmsChannelData.Type;

// Redo Chat channel data
export const RedoChatChannelData = Schema.Struct({
  _tag: Schema.Literal("redoChat"),
  contentType: Schema.String,
  structuredContent: Schema.NullOr(Schema.Unknown),
  confirmationState: Schema.NullOr(MessageRedoChatConfirmationState),
});
export type RedoChatChannelData = typeof RedoChatChannelData.Type;

// Voice channel data
export const VoiceChannelData = Schema.Struct({
  _tag: Schema.Literal("voice"),
  mongoCallRecordId: Schema.NullOr(MongoCallRecordId),
});
export type VoiceChannelData = typeof VoiceChannelData.Type;

// Facebook comment channel data
export const FacebookCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("facebookComment"),
  state: Schema.NullOr(MessageFacebookCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageFacebookCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type FacebookCommentChannelData = typeof FacebookCommentChannelData.Type;

// Instagram comment channel data
export const InstagramCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("instagramComment"),
  state: Schema.NullOr(MessageInstagramCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageInstagramCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type InstagramCommentChannelData =
  typeof InstagramCommentChannelData.Type;

// Meta comment channel data (for facebook and instagram comments) - DEPRECATED
export const MetaCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("metaComment"),
  type: MessageMetaCommentType,
  state: Schema.NullOr(MessageMetaCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageMetaCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type MetaCommentChannelData = typeof MetaCommentChannelData.Type;

// Facebook DM channel data
export const FacebookDmChannelData = Schema.Struct({
  _tag: Schema.Literal("facebookDm"),
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
});
export type FacebookDmChannelData = typeof FacebookDmChannelData.Type;

// Instagram DM channel data
export const InstagramDmChannelData = Schema.Struct({
  _tag: Schema.Literal("instagramDm"),
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
});
export type InstagramDmChannelData = typeof InstagramDmChannelData.Type;

// Internal note channel data
export const InternalNoteChannelData = Schema.Struct({
  _tag: Schema.Literal("internalNote"),
  htmlContent: Schema.NullOr(Schema.String),
  mentionedMongoUserIds: Schema.NullOr(Schema.Array(MongoUserId)),
});
export type InternalNoteChannelData = typeof InternalNoteChannelData.Type;

// Discriminated union of all message channel data types
export const MessageChannelData = Schema.Union(
  GmailChannelData,
  OutlookChannelData,
  SmsChannelData,
  RedoChatChannelData,
  VoiceChannelData,
  FacebookCommentChannelData,
  InstagramCommentChannelData,
  MetaCommentChannelData,
  FacebookDmChannelData,
  InstagramDmChannelData,
  InternalNoteChannelData,
);
export type MessageChannelData = typeof MessageChannelData.Type;
